<template>
  <div class="admin-ambassadors">
    <div class="page-header">
      <h2>{{ $t('legacy.Управление Амбасадорами') }}</h2>
      <p class="header-hint">{{ $t('legacy.Для назначения амбасадора перейдите в раздел Пользователи') }}</p>
    </div>

    <div class="ambassadors-table">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>{{ $t('legacy.Пользователь') }}</th>
            <th>Email</th>
            <th>{{ $t('legacy.Комиссия %') }}</th>
            <th>{{ $t('legacy.Лимит платежей') }}</th>
            <th>{{ $t('legacy.Рефералы') }}</th>
            <th>{{ $t('legacy.Баланс') }}</th>
            <th>{{ $t('legacy.Всего заработано') }}</th>
            <th>{{ $t('legacy.Статус') }}</th>
            <th>{{ $t('legacy.Действия') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="amb in ambassadors" :key="amb.id">
            <td>{{ amb.id }}</td>
            <td>{{ amb.name || amb.login || '-' }}</td>
            <td>{{ amb.email }}</td>
            <td>{{ amb.commission_percent }}%</td>
            <td>{{ amb.commission_payments_limit }}</td>
            <td>{{ amb.referrals_count || 0 }}</td>
             <td>{{ formatMoney(amb.current_balance) }}</td>
             <td>{{ formatMoney(amb.total_earned) }}</td>
            <td>
              <span :class="['status-badge', amb.is_active ? 'active' : 'inactive']">
                {{ amb.is_active ? $t('legacy.Активен') : $t('legacy.Неактивен') }}
              </span>
            </td>
            <td>
              <div class="actions">
                <button @click="viewAmbassador(amb)" class="btn-view">{{ $t('legacy.Детали') }}</button>
                <button @click="editAmbassador(amb)" class="btn-edit">{{ $t('legacy.Ред.') }}</button>
                <button @click="showPayoutModal(amb)" class="btn-payout">{{ $t('legacy.Выплата') }}</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="editingAmbassador" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-content">
        <h3>{{ $t('legacy.Редактировать Амбасадора') }}</h3>
        <form @submit.prevent="saveAmbassador">
          <div class="form-group">
            <label>{{ $t('legacy.Процент комиссии') }}</label>
            <input v-model.number="editForm.commissionPercent" type="number" min="0" max="100" step="0.01" required />
          </div>
          <div class="form-group">
            <label>{{ $t('legacy.Лимит оплачиваемых платежей') }}</label>
            <input v-model.number="editForm.commissionPaymentsLimit" type="number" min="1" max="999" required />
          </div>
          <div class="form-group">
            <label>
              <input v-model="editForm.isActive" type="checkbox" />
              {{ $t('legacy.Активен') }}
            </label>
          </div>
          <div class="form-actions">
            <button type="button" @click="closeEditModal" class="btn-cancel">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn-primary">{{ $t('common.save') }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="payoutAmbassador" class="modal-overlay" @click.self="closePayoutModal">
      <div class="modal-content">
        <h3>{{ $t('legacy.Выплата для') }} {{ payoutAmbassador.name || payoutAmbassador.email }}</h3>
         <p>{{ $t('legacy.Доступный баланс') }}: <strong>{{ formatMoney(payoutAmbassador.current_balance) }}</strong></p>
        <form @submit.prevent="processPayout">
          <div class="form-group">
            <label>{{ $t('legacy.Сумма выплаты') }}</label>
            <input v-model.number="payoutForm.amount" type="number" min="0" :max="payoutAmbassador.current_balance" step="0.01" required />
          </div>
          <div class="form-group">
            <label>{{ $t('legacy.Комментарий') }}</label>
            <textarea v-model="payoutForm.note" rows="2"></textarea>
          </div>
          <div class="form-actions">
            <button type="button" @click="closePayoutModal" class="btn-cancel">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn-primary" :disabled="payoutForm.amount <= 0">{{ $t('legacy.Провести выплату') }}</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="viewingAmbassador" class="modal-overlay" @click.self="closeViewModal">
      <div class="modal-content modal-wide">
        <h3>{{ $t('legacy.Детали Амбасадора') }}: {{ viewingAmbassador.ambassador?.name || viewingAmbassador.ambassador?.email }}</h3>
        
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-value">{{ viewingAmbassador.stats?.referrals_count || 0 }}</div>
            <div class="stat-label">{{ $t('legacy.Рефералов') }}</div>
          </div>
          <div class="stat-card">
             <div class="stat-value">{{ formatMoney(viewingAmbassador.stats?.current_balance) }}</div>
            <div class="stat-label">{{ $t('legacy.Баланс') }}</div>
          </div>
          <div class="stat-card">
             <div class="stat-value">{{ formatMoney(viewingAmbassador.stats?.total_earned) }}</div>
            <div class="stat-label">{{ $t('legacy.Всего заработано') }}</div>
          </div>
        </div>

        <div class="tabs">
          <button :class="['tab', { active: activeTab === 'promoCodes' }]" @click="activeTab = 'promoCodes'">{{ $t('legacy.Промо-коды') }}</button>
          <button :class="['tab', { active: activeTab === 'referrals' }]" @click="activeTab = 'referrals'">{{ $t('legacy.Рефералы') }}</button>
          <button :class="['tab', { active: activeTab === 'payouts' }]" @click="activeTab = 'payouts'">{{ $t('legacy.Выплаты') }}</button>
        </div>

        <div v-if="activeTab === 'promoCodes'" class="tab-content">
          <button @click="showPromoCodeModal = true" class="btn-primary btn-sm">+ {{ $t('legacy.Создать промо-код') }}</button>
          <table v-if="viewingAmbassador.promoCodes?.length" class="inner-table">
            <thead>
              <tr>
                <th>{{ $t('legacy.Код') }}</th>
                <th>{{ $t('legacy.Тип') }}</th>
                <th>{{ $t('legacy.Детали') }}</th>
                <th>{{ $t('legacy.Использований') }}</th>
                <th>{{ $t('legacy.Срок') }}</th>
                <th>{{ $t('legacy.Статус') }}</th>
                <th>{{ $t('legacy.Действия') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pc in viewingAmbassador.promoCodes" :key="pc.id">
                <td><code>{{ pc.code }}</code></td>
                <td>{{ pc.type === 'discount' ? $t('legacy.Скидка') : $t('legacy.Триал') }}</td>
                <td>
                  <template v-if="pc.type === 'discount'">{{ pc.discount_percent }}%</template>
                  <template v-else>{{ pc.trial_plan }} {{ $t('legacy.на') }} {{ pc.trial_days }} {{ $t('legacy.дн.') }}</template>
                </td>
                <td>{{ pc.current_usages }} / {{ pc.max_usages || '∞' }}</td>
                <td>{{ pc.expires_at ? formatDate(pc.expires_at) : $t('legacy.Бессрочно') }}</td>
                <td>
                  <span :class="['status-badge', pc.is_active ? 'active' : 'inactive']">
                    {{ pc.is_active ? $t('legacy.Активен') : $t('legacy.Неактивен') }}
                  </span>
                </td>
                <td>
                  <button @click="editPromoCode(pc)" class="btn-edit btn-sm">{{ $t('legacy.Ред.') }}</button>
                  <button @click="deletePromoCodeConfirm(pc)" class="btn-delete btn-sm">{{ $t('common.delete') }}</button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-data">{{ $t('legacy.Промо-кодов нет') }}</p>
        </div>

        <div v-if="activeTab === 'referrals'" class="tab-content">
          <table v-if="viewingAmbassador.referrals?.length" class="inner-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>{{ $t('legacy.Имя') }}</th>
                <th>{{ $t('legacy.Платежей') }}</th>
                <th>{{ $t('legacy.Дата привязки') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ref in viewingAmbassador.referrals" :key="ref.id">
                <td>{{ ref.email }}</td>
                <td>{{ ref.name || '-' }}</td>
                <td>{{ ref.payments_count }}</td>
                <td>{{ formatDate(ref.created_at) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-data">{{ $t('legacy.Рефералов нет') }}</p>
        </div>

        <div v-if="activeTab === 'payouts'" class="tab-content">
          <table v-if="viewingAmbassador.payouts?.length" class="inner-table">
            <thead>
              <tr>
                <th>{{ $t('legacy.Сумма') }}</th>
                <th>{{ $t('legacy.Комментарий') }}</th>
                <th>{{ $t('legacy.Провёл') }}</th>
                <th>{{ $t('legacy.Дата') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pay in viewingAmbassador.payouts" :key="pay.id">
                 <td>{{ formatMoney(pay.amount) }}</td>
                <td>{{ pay.note || '-' }}</td>
                <td>{{ pay.processed_by_name || '-' }}</td>
                <td>{{ formatDate(pay.created_at) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-data">{{ $t('legacy.Выплат нет') }}</p>
        </div>

        <div class="form-actions">
          <button @click="closeViewModal" class="btn-cancel">{{ $t('common.close') }}</button>
        </div>
      </div>
    </div>

    <div v-if="showPromoCodeModal" class="modal-overlay" @click.self="closePromoCodeModal">
      <div class="modal-content">
        <h3>{{ editingPromoCode ? $t('common.edit') : $t('common.create') }} {{ $t('legacy.промо-код') }}</h3>
        <form @submit.prevent="savePromoCode">
          <div class="form-group">
            <label>{{ $t('legacy.Код промо-кода') }}</label>
            <input v-model="promoCodeForm.code" type="text" required placeholder="SUMMER2026" />
          </div>
          <div class="form-group">
            <label>{{ $t('legacy.Тип') }}</label>
            <select v-model="promoCodeForm.type" required>
              <option value="discount">{{ $t('legacy.Скидка') }}</option>
              <option value="trial">{{ $t('legacy.Триал') }}</option>
            </select>
          </div>
          <div v-if="promoCodeForm.type === 'discount'" class="form-group">
            <label>{{ $t('legacy.Размер скидки (%)') }}</label>
            <input v-model.number="promoCodeForm.discountPercent" type="number" min="1" max="100" required />
          </div>
          <div v-if="promoCodeForm.type === 'trial'" class="form-group">
            <label>{{ $t('legacy.Тариф для триала') }}</label>
            <select v-model="promoCodeForm.trialPlan" required>
              <option value="base">{{ $t('legacy.Базовый') }}</option>
              <option value="premium">{{ $t('legacy.Премиум') }}</option>
            </select>
          </div>
          <div v-if="promoCodeForm.type === 'trial'" class="form-group">
            <label>{{ $t('legacy.Дней триала') }}</label>
            <input v-model.number="promoCodeForm.trialDays" type="number" min="1" max="365" required />
          </div>
          <div class="form-group">
            <label>
              <input v-model="promoCodeForm.isReusableByUser" type="checkbox" />
              {{ $t('legacy.Повторяемый для одного пользователя') }}
            </label>
          </div>
          <div class="form-group">
            <label>{{ $t('legacy.Максимум использований (0 = без лимита)') }}</label>
            <input v-model.number="promoCodeForm.maxUsages" type="number" min="0" />
          </div>
          <div class="form-group">
            <label>{{ $t('legacy.Срок действия') }}</label>
            <input v-model="promoCodeForm.expiresAt" type="date" />
          </div>
          <div class="form-group" v-if="editingPromoCode">
            <label>
              <input v-model="promoCodeForm.isActive" type="checkbox" />
              {{ $t('legacy.Активен') }}
            </label>
          </div>
          <div class="form-actions">
            <button type="button" @click="closePromoCodeModal" class="btn-cancel">{{ $t('common.cancel') }}</button>
            <button type="submit" class="btn-primary">{{ editingPromoCode ? $t('common.save') : $t('common.create') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { getAmbassadors, getAmbassadorById, updateAmbassador, processAmbassadorPayout, createPromoCode, updatePromoCode, deletePromoCode } from '@/api/ambassadors';
import { useToast } from 'vue-toastification';

export default {
  name: 'AdminAmbassadors',
  data() {
    return {
      toast: null,
      ambassadors: [],
      editingAmbassador: null,
      payoutAmbassador: null,
      viewingAmbassador: null,
      showPromoCodeModal: false,
      editingPromoCode: null,
      activeTab: 'promoCodes',
      editForm: {
        commissionPercent: 10,
        commissionPaymentsLimit: 1,
        isActive: true
      },
      payoutForm: {
        amount: 0,
        note: ''
      },
      promoCodeForm: {
        code: '',
        type: 'discount',
        discountPercent: 10,
        trialPlan: 'base',
        trialDays: 7,
        isReusableByUser: false,
        maxUsages: 0,
        expiresAt: '',
        isActive: true
      }
    };
  },
  async created() {
    this.toast = useToast();
    await this.loadAmbassadors();
  },
  methods: {
    async loadAmbassadors() {
      try {
        this.ambassadors = await getAmbassadors();
      } catch (error) {
        this.toast.error(this.$t('legacy.Ошибка загрузки амбасадоров'));
      }
    },
    formatMoney(value) {
       return this.$formatCurrency(parseFloat(value || 0), 'RUB', {
         minimumFractionDigits: 2,
         maximumFractionDigits: 2
       });
    },
    formatDate(date) {
      if (!date) return '-';
       return this.$formatDate(new Date(date));
    },
    editAmbassador(amb) {
      this.editingAmbassador = amb;
      this.editForm = {
        commissionPercent: parseFloat(amb.commission_percent),
        commissionPaymentsLimit: amb.commission_payments_limit,
        isActive: amb.is_active
      };
    },
    async saveAmbassador() {
      try {
        await updateAmbassador(this.editingAmbassador.id, this.editForm);
        this.toast.success(this.$t('legacy.Амбасадор обновлён'));
        this.closeEditModal();
        await this.loadAmbassadors();
      } catch (error) {
        this.toast.error(error.response?.data?.error || this.$t('legacy.Ошибка обновления'));
      }
    },
    async viewAmbassador(amb) {
      try {
        const data = await getAmbassadorById(amb.id);
        this.viewingAmbassador = data;
        this.activeTab = 'promoCodes';
      } catch (error) {
        this.toast.error(this.$t('legacy.Ошибка загрузки данных'));
      }
    },
    showPayoutModal(amb) {
      this.payoutAmbassador = amb;
      this.payoutForm = { amount: 0, note: '' };
    },
    async processPayout() {
      try {
        await processAmbassadorPayout(this.payoutAmbassador.id, this.payoutForm.amount, this.payoutForm.note);
        this.toast.success(this.$t('legacy.Выплата проведена'));
        this.closePayoutModal();
        await this.loadAmbassadors();
      } catch (error) {
        this.toast.error(error.response?.data?.error || this.$t('legacy.Ошибка выплаты'));
      }
    },
    editPromoCode(pc) {
      this.editingPromoCode = pc;
      this.promoCodeForm = {
        code: pc.code,
        type: pc.type,
        discountPercent: pc.discount_percent || 10,
        trialPlan: pc.trial_plan || 'base',
        trialDays: pc.trial_days || 7,
        isReusableByUser: pc.is_reusable_by_user,
        maxUsages: pc.max_usages || 0,
        expiresAt: pc.expires_at ? pc.expires_at.split('T')[0] : '',
        isActive: pc.is_active
      };
      this.showPromoCodeModal = true;
    },
    async savePromoCode() {
      try {
        const data = {
          code: this.promoCodeForm.code,
          type: this.promoCodeForm.type,
          discountPercent: this.promoCodeForm.type === 'discount' ? this.promoCodeForm.discountPercent : null,
          trialPlan: this.promoCodeForm.type === 'trial' ? this.promoCodeForm.trialPlan : null,
          trialDays: this.promoCodeForm.type === 'trial' ? this.promoCodeForm.trialDays : null,
          isReusableByUser: this.promoCodeForm.isReusableByUser,
          maxUsages: this.promoCodeForm.maxUsages || null,
          expiresAt: this.promoCodeForm.expiresAt || null,
          isActive: this.promoCodeForm.isActive
        };

        if (this.editingPromoCode) {
          await updatePromoCode(this.editingPromoCode.id, data);
          this.toast.success(this.$t('legacy.Промо-код обновлён'));
        } else {
          await createPromoCode(this.viewingAmbassador.ambassador.id, data);
          this.toast.success(this.$t('legacy.Промо-код создан'));
        }
        
        this.closePromoCodeModal();
        await this.viewAmbassador({ id: this.viewingAmbassador.ambassador.id });
      } catch (error) {
        this.toast.error(error.response?.data?.error || this.$t('legacy.Ошибка сохранения'));
      }
    },
    async deletePromoCodeConfirm(pc) {
       if (confirm(this.$t('legacy.dynamic.promoDelete', { code: pc.code }))) {
        try {
          await deletePromoCode(pc.id);
          this.toast.success(this.$t('legacy.Промо-код удалён'));
          await this.viewAmbassador({ id: this.viewingAmbassador.ambassador.id });
        } catch (error) {
          this.toast.error(this.$t('errors.delete'));
        }
      }
    },
    closeEditModal() {
      this.editingAmbassador = null;
    },
    closePayoutModal() {
      this.payoutAmbassador = null;
    },
    closeViewModal() {
      this.viewingAmbassador = null;
    },
    closePromoCodeModal() {
      this.showPromoCodeModal = false;
      this.editingPromoCode = null;
      this.promoCodeForm = {
        code: '',
        type: 'discount',
        discountPercent: 10,
        trialPlan: 'base',
        trialDays: 7,
        isReusableByUser: false,
        maxUsages: 0,
        expiresAt: '',
        isActive: true
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/global.scss";

.admin-ambassadors {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    margin: 0;
    color: #1F1E29;
  }
  
  .header-hint {
    margin: 0;
    color: #666;
    font-size: 14px;
    font-style: italic;
  }
}

.btn-primary {
  background: #BC9CFF;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #a080e5;
  }
  
  &.btn-sm {
    padding: 6px 12px;
    font-size: 12px;
    margin-bottom: 10px;
  }
}

.ambassadors-table {
  overflow-x: auto;
  
  table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background: #f5f5f5;
    font-weight: 600;
    color: #666;
  }
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &.active {
    background: #e8f5e9;
    color: #2e7d32;
  }
  
  &.inactive {
    background: #ffebee;
    color: #c62828;
  }
}

.actions {
  display: flex;
  gap: 8px;
  
  button {
    padding: 6px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    
    &.btn-view {
      background: #e3f2fd;
      color: #1565c0;
    }
    
    &.btn-edit {
      background: #fff3e0;
      color: #e65100;
    }
    
    &.btn-payout {
      background: #e8f5e9;
      color: #2e7d32;
    }
    
    &.btn-delete {
      background: #ffebee;
      color: #c62828;
    }
    
    &.btn-sm {
      padding: 4px 8px;
      font-size: 11px;
    }
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  
  &.modal-wide {
    max-width: 800px;
  }
  
  h3 {
    margin: 0 0 20px 0;
    color: #1F1E29;
  }
}

.form-group {
  margin-bottom: 16px;
  
  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #666;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: #BC9CFF;
    }
  }
  
  input[type="checkbox"] {
    width: auto;
    margin-right: 8px;
  }
  
  small {
    display: block;
    margin-top: 4px;
    color: #999;
    font-size: 12px;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn-cancel {
  background: #f5f5f5;
  color: #666;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  
  .stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #1F1E29;
  }
  
  .stat-label {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
  }
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.tab {
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  color: #666;
  font-weight: 500;
  border-radius: 4px;
  
  &.active {
    background: #BC9CFF;
    color: white;
  }
}

.tab-content {
  min-height: 200px;
}

.inner-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  th, td {
    padding: 8px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }
  
  th {
    background: #f5f5f5;
    font-weight: 600;
  }
  
  code {
    background: #f0f0f0;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
  }
}

.no-data {
  text-align: center;
  color: #999;
  padding: 40px;
}
</style>
